/**
 * SuperadminAriaInstances — Founder Insights Dashboard + lista cross-tenant
 * di tutti i widget Aria installati sui siti esterni.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, ExternalLink, Flame, ShieldAlert, TrendingUp, Gift } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const TIER_COLOR = {
    free: "#9EC3FF", welcome: "#86EFAC", full: "#A855F7", pro: "#A855F7", cinematic: "#FF1A5C",
};

const INTENT_COLOR = { hot: "#FF4D6D", warm: "#F5A623", cold: "#9EC3FF", unknown: "#555" };
const INTENT_LABEL = { hot: "caldo", warm: "tiepido", cold: "freddo", unknown: "sconosciuto" };

function StatCard({ label, value, hint, accent }) {
    return (
        <div className="rounded-xl px-4 py-3.5" style={{ background: "rgba(15,18,28,0.6)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`fi-stat-${label}`}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>{label}</div>
            <div className="text-[22px] mt-1" style={{ color: accent || "#fff", fontWeight: 500 }}>{value}</div>
            {hint && <div className="text-[10.5px] text-white/35 mt-0.5">{hint}</div>}
        </div>
    );
}

function IntentBar({ distribution }) {
    const total = Object.values(distribution || {}).reduce((a, b) => a + b, 0);
    if (!total) return <div className="text-white/35 text-[12px]" data-testid="fi-intent-bar" data-empty="true">Nessun lead negli ultimi 30 giorni.</div>;
    const keys = ["hot", "warm", "cold", "unknown"];
    return (
        <div data-testid="fi-intent-bar">
            <div className="flex w-full h-3 rounded-full overflow-hidden mb-2" style={{ background: "rgba(255,255,255,0.05)" }}>
                {keys.map((k) => {
                    const n = distribution[k] || 0;
                    if (!n) return null;
                    return <div key={k} style={{ width: `${(n / total) * 100}%`, background: INTENT_COLOR[k] }} title={`${k}: ${n}`} />;
                })}
            </div>
            <div className="flex gap-4 text-[11.5px] flex-wrap">
                {keys.map((k) => (distribution[k] || 0) > 0 && (
                    <span key={k} className="flex items-center gap-1.5 text-white/65">
                        <span className="w-2 h-2 rounded-full" style={{ background: INTENT_COLOR[k] }} />
                        {INTENT_LABEL[k]} · {distribution[k]}
                    </span>
                ))}
            </div>
        </div>
    );
}

function ChipList(props) {
    const { items, testId } = props;
    if (!items || !items.length) return <div className="text-white/35 text-[12px]" data-testid={testId} data-empty="true">Nessun dato nella finestra.</div>;
    return (
        <div className="flex flex-wrap gap-1.5" data-testid={testId}>
            {items.map((it) => (
                <span key={it.value} className="px-2.5 py-1 rounded-full text-[11px]" style={{ background: "rgba(168,85,247,0.12)", border: "1px solid rgba(168,85,247,0.3)", color: "#D8B4FE", fontFamily: "JetBrains Mono, monospace" }}>
                    {it.value} <span className="text-white/40">×{it.count}</span>
                </span>
            ))}
        </div>
    );
}

export default function SuperadminAriaInstances() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [error, setError] = useState("");
    const [insights, setInsights] = useState(null);
    const [insightsError, setInsightsError] = useState("");
    const [giftBusy, setGiftBusy] = useState(null);
    const [giftPick, setGiftPick] = useState({});

    const giftPlan = async (token) => {
        const tier = giftPick[token] || "cinematic";
        setGiftBusy(token);
        try {
            await api.post(`/superadmin/mc/aria/${token}/gift-plan`, { tier });
            setItems((prev) => prev.map((it) => it.widget_token === token ? { ...it, plan_tier: tier, experience_mode: tier, plan_source: "gift" } : it));
        } catch (e) { setError(formatApiError(e)); }
        finally { setGiftBusy(null); }
    };

    const revokeGift = async (token) => {
        setGiftBusy(token);
        try {
            await api.post(`/superadmin/mc/aria/${token}/revoke-gift`);
            setItems((prev) => prev.map((it) => it.widget_token === token ? { ...it, plan_tier: "free", experience_mode: "free", plan_source: "free" } : it));
        } catch (e) { setError(formatApiError(e)); }
        finally { setGiftBusy(null); }
    };

    useEffect(() => {
        document.title = "Aria Widget · Mission Control";
        (async () => {
            try {
                const { data } = await api.get("/superadmin/mc/aria/instances");
                setItems(data.items || []);
            } catch (e) { setError(formatApiError(e)); }
            setLoading(false);
        })();
        (async () => {
            try {
                const { data } = await api.get("/widget/superadmin/ecosystem-overview");
                setInsights(data);
            } catch (e) { setInsightsError(formatApiError(e)); }
        })();
    }, []);

    const filtered = items.filter((i) => filter === "all" || i.plan_tier === filter);
    const totals = insights?.totals || {};

    return (
        <div className="min-h-screen px-6 py-8" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="superadmin-aria">
            <div className="max-w-7xl mx-auto">
                <Link to="/superadmin" className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-6">
                    <ArrowLeft size={12} /> Mission Control
                </Link>

                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg,#A855F7,#7C3AED)" }}>
                            <Sparkles size={18} color="#fff" />
                        </div>
                        <div>
                            <h1 className="text-white text-[24px]" style={{ fontWeight: 400, letterSpacing: "-0.02em" }}>Founder Insights · Aria Widget</h1>
                            <div className="text-white/55 text-[12.5px]" style={{ fontFamily: "JetBrains Mono, monospace" }} data-testid="fi-header-count">
                                {loading ? "Caricamento istanze…" : `${items.length} istanze totali · ${items.filter(i => i.plan_tier !== "free").length} a pagamento`}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Founder Insights Dashboard ────────────────────────────── */}
                {insightsError && <div className="mb-4 p-3 rounded-xl text-[12px]" style={{ background: "rgba(255,26,92,0.1)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)" }}>{insightsError}</div>}
                {!insights && !insightsError && (
                    <div className="mb-8 text-white/35 text-[12.5px]" data-testid="fi-dashboard-loading" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        Caricamento Founder Insights…
                    </div>
                )}
                {insights && (
                    <div className="mb-8" data-testid="fi-dashboard">
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
                            <StatCard label="Installazioni" value={totals.installations ?? "—"} />
                            <StatCard label="Lead totali" value={totals.leads_total ?? "—"} />
                            <StatCard label="Lead 30gg" value={totals.leads_30d ?? "—"} />
                            <StatCard label="Conversion 30gg" value={`${totals.conversion_rate_30d_pct ?? 0}%`} accent="#86EFAC" />
                            <StatCard label="Upgrade in attesa" value={totals.plan_intents_pending ?? 0} accent={totals.plan_intents_pending ? "#F5A623" : undefined} hint={totals.plan_intents_pending ? "da approvare" : undefined} />
                            <StatCard label="Eventi sicurezza 30gg" value={totals.security_events_30d ?? 0} accent={totals.security_events_30d ? "#FF4D6D" : undefined} />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <div className="rounded-2xl p-4" style={{ background: "rgba(15,18,28,0.6)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/45 mb-3" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                    <Flame size={13} /> Intent dei lead (30gg, tutti i tenant)
                                </div>
                                <IntentBar distribution={insights.intent_distribution_30d} />
                            </div>

                            <div className="rounded-2xl p-4" style={{ background: "rgba(15,18,28,0.6)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/45 mb-3" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                    <TrendingUp size={13} /> Temi ricorrenti nelle richieste (30gg)
                                </div>
                                <ChipList items={insights.top_tags_30d} testId="fi-top-tags" />
                            </div>

                            <div className="rounded-2xl p-4" style={{ background: "rgba(15,18,28,0.6)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/45 mb-3" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                    <ShieldAlert size={13} /> Bisogni futuri inferiti (cross-sell, 30gg)
                                </div>
                                <ChipList items={insights.top_future_needs_30d} testId="fi-top-future-needs" />
                            </div>
                        </div>

                        {insights.top_installations_30d?.length > 0 && (
                            <div className="rounded-2xl p-4 mt-4" style={{ background: "rgba(15,18,28,0.6)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 mb-3" style={{ fontFamily: "JetBrains Mono, monospace" }}>Top 10 siti per lead (30gg)</div>
                                <div className="flex flex-col gap-1.5" data-testid="fi-top-installations">
                                    {insights.top_installations_30d.map((row, idx) => (
                                        <div key={row.widget_token} className="flex items-center justify-between text-[12.5px] py-1 border-b border-white/[0.04] last:border-0">
                                            <span className="text-white/75">#{idx + 1} {row.site_name || row.widget_token}</span>
                                            <span className="text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>{row.leads_30d} lead</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Filtri tier */}
                <div className="flex items-center gap-2 mb-5 flex-wrap">
                    {["all", "free", "welcome", "full", "pro", "cinematic"].map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => setFilter(t)}
                            className={`px-3.5 py-1.5 rounded-full text-[11.5px] uppercase tracking-[0.18em] transition`}
                            style={{
                                background: filter === t ? (TIER_COLOR[t] || "#fff") : "rgba(255,255,255,0.04)",
                                color: filter === t ? "#05050F" : "#fff",
                                fontFamily: "JetBrains Mono, monospace",
                                fontWeight: filter === t ? 600 : 400,
                            }}
                            data-testid={`tier-filter-${t}`}
                        >
                            {t}
                        </button>
                    ))}
                </div>

                {error && <div className="mb-4 p-3 rounded-xl text-[12px]" style={{ background: "rgba(255,26,92,0.1)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)" }}>{error}</div>}

                <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(15,18,28,0.6)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <table className="w-full text-[12.5px]">
                        <thead>
                            <tr className="text-left text-[10.5px] uppercase tracking-[0.22em] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                <th className="px-5 py-3.5">Nome</th>
                                <th className="px-5 py-3.5">Dominio</th>
                                <th className="px-5 py-3.5">Owner</th>
                                <th className="px-5 py-3.5">Piano</th>
                                <th className="px-5 py-3.5">Subscription</th>
                                <th className="px-5 py-3.5">Regala un piano</th>
                                <th className="px-5 py-3.5"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan="7" className="px-5 py-8 text-center text-white/45">Caricamento…</td></tr>}
                            {!loading && filtered.length === 0 && <tr><td colSpan="7" className="px-5 py-8 text-center text-white/45">Nessun widget per questo filtro</td></tr>}
                            {filtered.map((w) => (
                                <tr key={w.widget_token} className="border-t border-white/[0.04] hover:bg-white/[0.02]" data-testid={`aria-row-${w.widget_token}`}>
                                    <td className="px-5 py-3.5 text-white">{w.name || "—"}</td>
                                    <td className="px-5 py-3.5 text-white/75 text-[11.5px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{w.domain || "—"}</td>
                                    <td className="px-5 py-3.5 text-white/75">{w.owner_email || "—"}</td>
                                    <td className="px-5 py-3.5">
                                        <span className="text-[10.5px] uppercase tracking-[0.2em] px-2 py-0.5 rounded" style={{ background: (TIER_COLOR[w.plan_tier] || "#9EC3FF") + "22", color: TIER_COLOR[w.plan_tier] || "#9EC3FF", fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }}>
                                            {w.plan_tier}
                                        </span>
                                        {w.plan_source === "gift" && (
                                            <span className="ml-1.5 text-[9.5px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded" style={{ background: "rgba(245,166,35,0.15)", color: "#F5A623", fontFamily: "JetBrains Mono, monospace" }} data-testid={`gift-badge-${w.widget_token}`}>
                                                regalo
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-5 py-3.5 text-[11.5px] text-white/65" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                        {w.subscription_status ? (
                                            <span>{w.subscription_status} · {w.provider} · {w.billing_cycle}</span>
                                        ) : <span className="text-white/30">—</span>}
                                    </td>
                                    <td className="px-5 py-3.5">
                                        {w.plan_source === "stripe" || w.plan_source === "paypal" ? (
                                            <span className="text-[10.5px] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }} data-testid={`gift-locked-${w.widget_token}`}>
                                                cliente pagante — non regalabile
                                            </span>
                                        ) : w.plan_source === "gift" ? (
                                            <button
                                                type="button"
                                                disabled={giftBusy === w.widget_token}
                                                onClick={() => revokeGift(w.widget_token)}
                                                className="text-[11px] px-3 py-1.5 rounded-full transition disabled:opacity-50"
                                                style={{ background: "rgba(255,26,92,0.12)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)", fontFamily: "JetBrains Mono, monospace" }}
                                                data-testid={`revoke-gift-${w.widget_token}`}
                                            >
                                                {giftBusy === w.widget_token ? "…" : "Disattiva → free"}
                                            </button>
                                        ) : (
                                            <div className="flex items-center gap-1.5">
                                                <select
                                                    value={giftPick[w.widget_token] || "cinematic"}
                                                    onChange={(e) => setGiftPick((p) => ({ ...p, [w.widget_token]: e.target.value }))}
                                                    className="text-[11px] rounded-lg px-2 py-1.5 bg-transparent"
                                                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", fontFamily: "JetBrains Mono, monospace" }}
                                                    data-testid={`gift-select-${w.widget_token}`}
                                                >
                                                    <option value="welcome" style={{ color: "#000" }}>welcome</option>
                                                    <option value="full" style={{ color: "#000" }}>full</option>
                                                    <option value="cinematic" style={{ color: "#000" }}>cinematic</option>
                                                </select>
                                                <button
                                                    type="button"
                                                    disabled={giftBusy === w.widget_token}
                                                    onClick={() => giftPlan(w.widget_token)}
                                                    className="text-[11px] px-3 py-1.5 rounded-full transition disabled:opacity-50 inline-flex items-center gap-1"
                                                    style={{ background: "rgba(245,166,35,0.14)", color: "#F5A623", border: "1px solid rgba(245,166,35,0.35)", fontFamily: "JetBrains Mono, monospace" }}
                                                    data-testid={`gift-btn-${w.widget_token}`}
                                                >
                                                    <Gift size={11} /> {giftBusy === w.widget_token ? "…" : "Regala"}
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-5 py-3.5 text-right">
                                        {w.widget_token && (
                                            <Link to={`/aria-widget/config/${w.widget_token}`} className="inline-flex items-center gap-1 text-[11.5px] text-white/55 hover:text-white">
                                                config <ExternalLink size={10} />
                                            </Link>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
